import type { Polygon } from "geojson";

/** Precisión y redondeo de coordenadas del wizard de finca.
 *
 *  **Qué había acá y por qué ya no está (2026-10-09).** Este archivo contenía
 *  `AoiPolygonUnion` y `fx_calcularPerimetroGlobal`: la unión topológica que
 *  fundía las piezas del área en un solo polígono, tendiendo puentes
 *  bufferizados entre los bloques separados (~650 líneas de turf, portadas del
 *  control original `finalizeUserDrawing`). **Ese cálculo vive ahora en el
 *  servidor** (`PerimetroGlobalGeometria`, en `Api/Grpc/Cliente/`): el cliente
 *  manda las PIEZAS con `guardarAoiAsync` y el perímetro sale de ahí.
 *
 *  Se movió porque era el último trabajo pesado del hilo principal: con un
 *  área importada de un KML, unir decenas de piezas tardaba segundos, y como
 *  el "Continuar" del paso 3 espera el guardado, la pantalla se congelaba
 *  justo en el cambio de paso. Con él se fueron `AoiDrawToolbar.getFinalGeometry`
 *  y `calcularGeometriaFinal`, que eran sus únicos llamadores, y
 *  `layerToTurfPolygon`, que ya no usaba nadie.
 *
 *  **El archivo conserva el nombre** aunque ya no una nada: lo importan cinco
 *  módulos por el redondeo, y renombrarlo sería ruido en todos ellos para no
 *  ganar nada. Lo que queda es la precisión con la que se guarda TODA
 *  coordenada del wizard.
 *
 *  7 decimales (~1 cm) no es un número elegido al azar: es el mismo valor con
 *  el que el servidor guarda las piezas y sus vértices
 *  (`Constantes.RegistroFinca.DecimalesCoordenadas`) y con el que GeoServer
 *  publica esas capas (`Config.DECIMALES_POR_CAPA`). Los tres tienen que
 *  coincidir: si lo guardado tuviera más precisión que lo que el cliente lee,
 *  cada apertura del modo Editar compararía su pieza contra una versión apenas
 *  distinta de sí misma y la daría por ajena.
 *
 *  Las tolerancias que existen alrededor (10 cm para extender un corte, para
 *  reintentar una unión o para sellar un borde) están un orden de magnitud por
 *  encima de este redondeo, así que lo absorben sin problema. */
export const PRECISION_COORDENADAS = 7;

const FACTOR_PRECISION = 10 ** PRECISION_COORDENADAS;

/** Redondea una coordenada suelta (lat o lng) a `PRECISION_COORDENADAS`. */
export function fx_redondearCoordenada(valor: number): number {
  return Math.round(valor * FACTOR_PRECISION) / FACTOR_PRECISION;
}

/** Redondea todas las coordenadas de un polígono. Puede dejar vértices
 *  consecutivos idénticos (dos que estaban a menos de 1 cm colapsan en uno):
 *  de eso se encargan las pasadas de limpieza de `LoteSplitState`. */
export function fx_truncarPolygon(polygon: Polygon): Polygon {
  return {
    ...polygon,
    coordinates: polygon.coordinates.map((anillo) =>
      anillo.map((punto) => [
        fx_redondearCoordenada(punto[0]),
        fx_redondearCoordenada(punto[1]),
        ...punto.slice(2),
      ]),
    ),
  };
}
